import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Chip, Table, TableBody, TableCell, TableHead, TableRow } from '@mui/material';
import { Section } from '../../../../components/ui/Section';
import { CatalogueGovernanceClient, OffContractRequestRow } from '../../../../app/services/api/catalogueGovernance';
import ApprovalStatusChip from './ApprovalStatusChip';
import RowActionsMenu from './RowActionsMenu';

const URGENCY_COLOR: Record<OffContractRequestRow['urgency'], 'default' | 'warning' | 'error'> = {
  Low: 'default',
  Medium: 'warning',
  High: 'error',
};

const OffContractRequestsTab = () => {
  const { t } = useTranslation('catalogueGovernance');
  const [rows, setRows] = useState<OffContractRequestRow[]>([]);
  const [suggestedIds, setSuggestedIds] = useState<Set<string>>(new Set());
  const [onboardingIds, setOnboardingIds] = useState<Set<string>>(new Set());

  const load = () => CatalogueGovernanceClient.getOffContractRequests().then(setRows);

  useEffect(() => {
    load();
  }, []);

  const handleAction = async (action: (id: string) => Promise<void>, id: string) => {
    await action(id);
    load();
  };

  const handleSuggestAlternative = async (id: string) => {
    await CatalogueGovernanceClient.suggestAlternative(id);
    setSuggestedIds((prev) => new Set(prev).add(id));
  };

  const handleCreateOnboarding = async (id: string) => {
    await CatalogueGovernanceClient.createSupplierOnboardingRequest(id);
    setOnboardingIds((prev) => new Set(prev).add(id));
  };

  const isDecided = (status: OffContractRequestRow['approvalStatus']) => status !== 'Pending';

  return (
    <Section>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>{t('offContractRequests.columnSite', 'Site')}</TableCell>
            <TableCell>{t('offContractRequests.columnRequestedItem', 'Requested item')}</TableCell>
            <TableCell>{t('offContractRequests.columnSupplier', 'Supplier')}</TableCell>
            <TableCell>{t('offContractRequests.columnReason', 'Reason')}</TableCell>
            <TableCell>{t('offContractRequests.columnUrgency', 'Urgency')}</TableCell>
            <TableCell align="right">{t('offContractRequests.columnEstimatedValue', 'Estimated value')}</TableCell>
            <TableCell>{t('offContractRequests.columnExistingApprovedAlternative', 'Existing approved alternative')}</TableCell>
            <TableCell>{t('offContractRequests.columnRequestedBy', 'Requested by')}</TableCell>
            <TableCell>{t('offContractRequests.columnApprovalStatus', 'Approval status')}</TableCell>
            <TableCell>{t('offContractRequests.columnSubmittedDate', 'Submitted date')}</TableCell>
            <TableCell>{t('offContractRequests.columnActions', 'Actions')}</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.length === 0 && (
            <TableRow><TableCell colSpan={11}>{t('offContractRequests.emptyState', 'No off-contract requests to show.')}</TableCell></TableRow>
          )}
          {rows.map((row) => (
            <TableRow key={row.id}>
              <TableCell>{row.site}</TableCell>
              <TableCell>{row.requestedItem}</TableCell>
              <TableCell>{row.supplier}</TableCell>
              <TableCell>{row.reason}</TableCell>
              <TableCell><Chip label={t(`chips.severity.${row.urgency}`, row.urgency)}
                size="small"
                color={URGENCY_COLOR[row.urgency]} /></TableCell>
              <TableCell align="right">£{row.estimatedValue.toFixed(2)}</TableCell>
              <TableCell>{row.existingApprovedAlternative ?? '-'}</TableCell>
              <TableCell>{row.requestedBy}</TableCell>
              <TableCell><ApprovalStatusChip status={row.approvalStatus}
                label={t(`chips.status.${row.approvalStatus}`, row.approvalStatus)} /></TableCell>
              <TableCell>{row.submittedDate}</TableCell>
              <TableCell>
                <RowActionsMenu actions={[
                  {
                    label: t('offContractRequests.actionApproveOnce', 'Approve once'),
                    disabled: isDecided(row.approvalStatus),
                    onClick: () => handleAction(CatalogueGovernanceClient.approveOffContractOnce.bind(CatalogueGovernanceClient), row.id),
                  },
                  {
                    label: t('offContractRequests.actionApproveAndAdd', 'Approve & add to catalogue'),
                    disabled: isDecided(row.approvalStatus),
                    onClick: () => handleAction(
                      CatalogueGovernanceClient.approveOffContractAndAddToCatalogue.bind(CatalogueGovernanceClient), row.id,
                    ),
                  },
                  {
                    label: t('offContractRequests.actionReject', 'Reject'),
                    disabled: isDecided(row.approvalStatus),
                    destructive: true,
                    onClick: () => handleAction(CatalogueGovernanceClient.rejectOffContractRequest.bind(CatalogueGovernanceClient), row.id),
                  },
                  {
                    label: suggestedIds.has(row.id)
                      ? t('offContractRequests.actionAlternativeSuggested', 'Alternative suggested')
                      : t('offContractRequests.actionSuggestAlternative', 'Suggest alternative'),
                    disabled: suggestedIds.has(row.id),
                    onClick: () => handleSuggestAlternative(row.id),
                  },
                  {
                    label: onboardingIds.has(row.id)
                      ? t('offContractRequests.actionOnboardingRequested', 'Onboarding requested')
                      : t('offContractRequests.actionCreateOnboarding', 'Create supplier onboarding request'),
                    disabled: onboardingIds.has(row.id),
                    onClick: () => handleCreateOnboarding(row.id),
                  },
                ]} />
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Section>
  );
};

export default OffContractRequestsTab;
